import { StoreTweet } from '@/class/StoreTweet';
import { HTTPResponse } from '@/class/HTTPResponse';
import { deleteData, fetchData, postData, putData } from '../../../../lib/api';
import { useAuthContext } from '../../../../context/AuthContext';
import AdminLayout from '../../../layout/AdminLayout';
import TweetCreateModal from './createModal';
import TweetEditModal from './editModal';
import { emptyTweetForm, parseTweetForm, tweetToForm, TweetForm } from './tweetForm';
import {
  adminCard,
  adminSectionTitle,
  adminTableWrap,
  adminTable,
  adminTableHead,
  adminTableCell,
} from '../../../layout/adminStyles';
import React, { useCallback, useEffect, useState } from 'react';

const formatDateParts = (value?: string): { date: string; time: string } | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    date: date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }),
    time: date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

const tweetButtonBase =
  'inline-flex items-center justify-center rounded-md px-2 py-1 text-[11px] leading-tight font-medium whitespace-nowrap transition-colors';

const tweetToolbarButton =
  'inline-flex items-center justify-center rounded-lg bg-teal-500 hover:bg-teal-600 text-white px-3 py-1.5 text-xs font-medium whitespace-nowrap shadow-sm transition-colors';

const tweetPrimaryButton = `${tweetButtonBase} bg-teal-500 hover:bg-teal-600 text-white shadow-sm`;

const tweetDangerButton = `${tweetButtonBase} border border-red-300 text-red-600 hover:bg-red-50`;

const AdminStoreTweetPage = () => {
  const { store } = useAuthContext();
  const [tweets, setTweets] = useState<StoreTweet[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [editingTweetId, setEditingTweetId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<TweetForm>(emptyTweetForm());
  const [createForm, setCreateForm] = useState<TweetForm>(emptyTweetForm());
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const storeId = store?.id;

  const loadTweets = useCallback(async () => {
    if (!storeId) {
      setTweets([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res: HTTPResponse = await fetchData(`store/${storeId}/tweet`);
      setTweets(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error(err);
      setTweets([]);
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadTweets();
  }, [loadTweets]);

  const openEditModal = (tweet: StoreTweet) => {
    setEditingTweetId(tweet.id);
    setEditForm(tweetToForm(tweet));
    setIsShowEditModal(true);
  };

  const closeEditModal = () => {
    setIsShowEditModal(false);
    setEditingTweetId(null);
    setEditForm(emptyTweetForm());
  };

  const openCreateModal = () => {
    setCreateForm(emptyTweetForm());
    setIsShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setIsShowCreateModal(false);
    setCreateForm(emptyTweetForm());
  };

  const handleSave = async () => {
    if (editingTweetId == null || !storeId) {
      return;
    }

    const payload = parseTweetForm(editForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await putData(`store/${storeId}/tweet/${editingTweetId}`, payload);

      if (res.data) {
        const updated = res.data as StoreTweet;
        setTweets((prev) =>
          prev.map((tweet) => (tweet.id === editingTweetId ? updated : tweet))
        );
        closeEditModal();
      }
    } catch (err: unknown) {
      console.error(err);
      alert('つぶやきの更新に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!storeId) {
      return;
    }

    const payload = parseTweetForm(createForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await postData(`store/${storeId}/tweet`, payload);

      if (res.data) {
        const created = res.data as StoreTweet;
        setTweets((prev) => [created, ...prev].slice(0, 20));
        closeCreateModal();
      }
    } catch (err: unknown) {
      console.error(err);
      alert('つぶやきの投稿に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (tweet: StoreTweet) => {
    if (!storeId) {
      return;
    }

    if (!window.confirm('このつぶやきを削除しますか？')) {
      return;
    }

    try {
      await deleteData(`store/${storeId}/tweet/${tweet.id}`);
      setTweets((prev) => prev.filter((item) => item.id !== tweet.id));
    } catch (err: unknown) {
      console.error(err);
      alert('つぶやきの削除に失敗しました。');
    }
  };

  return (
    <AdminLayout>
      <div className={adminCard}>
        <div className="mb-4 flex flex-row items-center justify-between gap-2 pb-3 sm:mb-5">
          <h2 className={adminSectionTitle + ' mb-0 min-w-0 pb-0'}>つぶやき</h2>
          <button
            type="button"
            className={tweetToolbarButton}
            onClick={openCreateModal}
            disabled={!storeId}
          >
            新規投稿
          </button>
        </div>
        <p className="mb-4 text-sm text-stone-600">直近20件を表示しています（{tweets.length}件）</p>

        <div className={adminTableWrap}>
          <table className={`${adminTable} table-auto sm:table-fixed`}>
            <thead>
              <tr className={adminTableHead}>
                <th className={`${adminTableCell} text-left w-24 sm:w-36 whitespace-normal`}>投稿日時</th>
                <th className="border border-stone-200 p-2 sm:p-3 text-left text-sm sm:text-base text-stone-700 font-medium whitespace-normal">
                  つぶやき
                </th>
                <th className={`${adminTableCell} text-center w-20 sm:w-28`}>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={3} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                    読み込み中...
                  </td>
                </tr>
              ) : tweets.length === 0 ? (
                <tr>
                  <td colSpan={3} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                    つぶやきがありません。
                  </td>
                </tr>
              ) : (
                tweets.map((tweet) => {
                  const dateParts = formatDateParts(tweet.created_at);

                  return (
                  <tr key={tweet.id}>
                    <td className={`${adminTableCell} whitespace-normal text-xs sm:text-sm`}>
                      {dateParts ? (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1 leading-snug">
                          <span>{dateParts.date}</span>
                          <span className="text-stone-500 sm:text-stone-700">{dateParts.time}</span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="border border-stone-200 p-2 sm:p-3 text-stone-700 text-xs sm:text-sm whitespace-normal break-words leading-relaxed max-w-0">
                      {tweet.body}
                    </td>
                    <td className={`${adminTableCell} text-center`}>
                      <div className="flex flex-row flex-wrap items-center justify-center gap-1">
                        <button
                          type="button"
                          className={tweetPrimaryButton}
                          onClick={() => openEditModal(tweet)}
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          className={tweetDangerButton}
                          onClick={() => handleDelete(tweet)}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <TweetEditModal
        isShowModal={isShowEditModal}
        form={editForm}
        isSaving={isSaving}
        onChange={setEditForm}
        onClose={closeEditModal}
        onSubmit={handleSave}
      />

      <TweetCreateModal
        isShowModal={isShowCreateModal}
        form={createForm}
        isSaving={isSaving}
        onChange={setCreateForm}
        onClose={closeCreateModal}
        onSubmit={handleCreate}
      />
    </AdminLayout>
  );
};

export default AdminStoreTweetPage;
